import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get correct image URL
export const getImageUrl = (imageUrl) => {
  if (!imageUrl) return null;
  if (imageUrl.startsWith('/uploads')) {
    // Use /api/uploads path to route through Kubernetes ingress to backend
    return `${API_URL}/api${imageUrl}`;
  }
  return imageUrl;
};

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const auth = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  requestOTP: (data) => api.post('/auth/login/request-otp', data),
  verifyOTP: (data) => api.post('/auth/login/verify-otp', data),
  getProfile: () => api.get('/auth/me'),
  updateProfile: (data) => api.put('/auth/profile', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Credit Applications
export const creditApplications = {
  getMine: () => api.get('/credit-applications/my'),
  submit: (data) => api.post('/credit-applications', data),
  // Admin
  list: (params) => api.get('/admin/credit-applications', { params }),
  get: (id) => api.get(`/admin/credit-applications/${id}`),
  approve: (id, approvedLimit, paymentTermsDays) =>
    api.post(`/admin/credit-applications/${id}/approve`, {
      approved_limit: approvedLimit,
      payment_terms_days: paymentTermsDays,
    }),
  reject: (id, reason) =>
    api.post(`/admin/credit-applications/${id}/reject`, { reason }),
};

// Categories
export const categories = {
  getAll: () => api.get('/categories'),
  create: (data) => api.post('/categories', data),
  update: (id, data) => api.put(`/categories/${id}`, data),
};

// Products
export const products = {
  getAll: (params) => api.get('/products', { params }),
  getById: (id) => api.get(`/products/${id}`),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
  updateInventory: (id, inventory) => api.put(`/products/${id}/inventory`, inventory),
  getOutOfStock: () => api.get('/products/out-of-stock'),
};

// Order Minimums
export const orderMinimums = {
  get: () => api.get('/order-minimums'),
};

// Cart
export const cart = {
  get: () => api.get('/cart'),
  add: (data) => api.post('/cart/add', data),
  update: (productId, data) => api.put(`/cart/update/${productId}`, data),
  remove: (productId) => api.delete(`/cart/remove/${productId}`),
  clear: () => api.delete('/cart/clear'),
  validate: () => api.get('/cart/validate'),
  bulkAdd: (items) => api.post('/cart/bulk-add', { items }),
  sync: (items) => api.post('/cart/sync', { items }),
};

// Banners (public)
export const banners = {
  getActive: () => api.get('/banners'),
};

// Addresses
export const addresses = {
  getAll: () => api.get('/addresses'),
  create: (data) => api.post('/addresses', data),
  update: (id, data) => api.put(`/addresses/${id}`, data),
  delete: (id) => api.delete(`/addresses/${id}`),
  setDefault: (id) => api.put(`/addresses/${id}/default`),
};

// Wishlist
export const wishlist = {
  getAll: () => api.get('/wishlist'),
  getIds: () => api.get('/wishlist/ids'),
  add: (productId) => api.post(`/wishlist/add/${productId}`),
  remove: (productId) => api.delete(`/wishlist/remove/${productId}`),
  check: (productId) => api.get(`/wishlist/check/${productId}`),
};

// Orders
export const orders = {
  create: (data) => api.post('/orders', data),
  getAll: (params) => api.get('/orders', { params }),
  getById: (id) => api.get(`/orders/${id}`),
  updateStatus: (id, status) => api.put(`/orders/${id}/status`, { status }),
  delete: (id) => api.delete(`/admin/orders/${id}`),
  bulkDelete: (orderIds) => api.delete('/admin/orders/bulk', { data: { order_ids: orderIds } }),
  reorder: (orderId) => api.post(`/orders/${orderId}/reorder`),
  downloadPOS: (orderId) => `${API_URL}/api/orders/${orderId}/download-pos`,
  downloadPDF: (orderId) => `${API_URL}/api/orders/${orderId}/download-pdf`,
};

// Stock Lots
export const stockLots = {
  getDetails: (productId) => api.get(`/stock-lot/${productId}`),
  purchase: (data) => api.post('/stock-lot/purchase', data),
};

// Admin
export const admin = {
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (id) => api.get(`/admin/users/${id}`),
  updateUser: (id, data) => api.put(`/admin/users/${id}`, data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  getStats: () => api.get('/admin/stats'),
  // Credit management
  resetUserCredit: (userId, amount) => api.post(`/admin/users/${userId}/credit/reset`, { amount }),
  recordPayment: (userId, data) => api.post(`/admin/users/${userId}/payment/record`, data),
  getUserPayments: (userId) => api.get(`/admin/users/${userId}/payments`),
  getAllPayments: (params) => api.get('/admin/payments', { params }),
  // Inventory management
  getInventory: () => api.get('/admin/inventory'),
  updateInventory: (data) => api.put('/admin/inventory/update', data),
  bulkUpdateInventory: (updates) => api.put('/admin/inventory/bulk', { updates }),
  exportInventoryCSV: () => api.get('/admin/inventory/export', { responseType: 'blob' }),
  importInventoryCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/inventory/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Pricing management
  getPricing: () => api.get('/admin/pricing'),
  updateProductPrice: (productId, price, customPricing = false) => 
    api.put(`/admin/pricing/product/${productId}?price=${price}&custom_pricing=${customPricing}`),
  updateCategoryPricing: (data) => api.put('/admin/pricing/category', data),
  // Product management
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  cloneProduct: (id) => api.post(`/admin/products/${id}/clone`),
  assignProductCategory: (productId, categorySlug) => 
    api.put(`/admin/products/${productId}/category?category_slug=${categorySlug}`),
  bulkAssignCategory: (productIds, categorySlug) => 
    api.put(`/admin/products/bulk-category?category_slug=${categorySlug}`, productIds),
  bulkUpdateProducts: (productIds, updateData) => 
    api.put('/admin/products/bulk-update', { product_ids: productIds, update_data: updateData }),
  repairInventorySizes: () => api.post('/admin/products/repair-inventory-sizes'),
  // Out of Stock management
  getOutOfStockProducts: () => api.get('/admin/products/out-of-stock'),
  toggleOutOfStock: (productId, isOutOfStock) => 
    api.put(`/admin/products/${productId}/out-of-stock?is_out_of_stock=${isOutOfStock}`),
  bulkToggleOutOfStock: (productIds, isOutOfStock) => 
    api.put(`/admin/products/bulk-out-of-stock?is_out_of_stock=${isOutOfStock}`, productIds),
  
  // General image upload
  uploadGeneralImage: async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await fetch(`${API_URL}/api/admin/upload-image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('token')}`
      },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },

  // Banner management
  getBanners: () => api.get('/admin/banners'),
  createBanner: async (formData) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/banners`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }
    return response.json();
  },
  updateBanner: async (bannerId, formData) => {
    const token = localStorage.getItem('token');
    const response = await fetch(`${API_URL}/api/admin/banners/${bannerId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Update failed');
    }
    return response.json();
  },
  deleteBanner: (bannerId) => api.delete(`/admin/banners/${bannerId}`),

  // Product CSV import/export
  exportProductsCSV: () => api.get('/admin/products/export', { responseType: 'blob' }),
  downloadProductTemplate: () => api.get('/admin/products/template', { responseType: 'blob' }),
  importProductsCSV: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/products/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Barcode import
  importBarcodes: (formData) => {
    return api.post('/admin/barcodes/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  // Image management
  uploadImage: async (productId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    const token = localStorage.getItem('token');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    
    const response = await fetch(`${API_URL}/api/admin/products/${productId}/images`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`
      },
      body: formData
    });
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }
    
    const data = await response.json();
    console.log('Upload API response:', data);
    return { data };
  },
  deleteImage: (productId, imageUrl) => 
    api.delete(`/admin/products/${productId}/images?image_url=${encodeURIComponent(imageUrl)}`),
  reorderImages: (productId, images) => 
    api.put(`/admin/products/${productId}/images/reorder`, images),
  // Bulk tag updates
  bulkUpdateTags: (productIds, tag, value) => 
    api.post('/admin/products/bulk-tags', { product_ids: productIds, tag, value }),
  // Category management
  getCategories: () => api.get('/admin/categories'),
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  // Business settings
  getBusinessSettings: () => api.get('/settings/business'),
  updateBusinessSettings: (data) => api.put('/admin/settings/business', data),
  // Analytics
  getAnalytics: (days = 30) => api.get(`/admin/analytics?days=${days}`),
  exportAnalyticsCSV: (days = 30) => api.get(`/admin/analytics/export?days=${days}`, { responseType: 'blob' }),
};

// Business Settings (Public)
export const settings = {
  getBusiness: () => api.get('/settings/business'),
};

// Payments (Buyer)
export const payments = {
  // Legacy Stripe
  createCheckout: (originUrl) => api.post('/payments/checkout', { origin_url: originUrl }),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
  getHistory: () => api.get('/payments/history'),
  // PayPal
  getPayPalConfig: () => api.get('/payments/paypal/config'),
  createPayPalOrder: (originUrl, orderIds = null) => api.post('/payments/paypal/create-order', { 
    origin_url: originUrl,
    order_ids: orderIds 
  }),
  capturePayPalOrder: (orderId) => api.post(`/payments/paypal/capture/${orderId}`),
  getPayPalStatus: (orderId) => api.get(`/payments/paypal/status/${orderId}`),
};

// Activity Tracking
export const activity = {
  track: (data) => api.post('/track/activity', data),
  getDashboard: () => api.get('/admin/activity/dashboard'),
  getRecent: (limit = 50, eventType = null) => {
    const params = { limit };
    if (eventType) params.event_type = eventType;
    return api.get('/admin/activity/recent', { params });
  },
  getLiveVisitors: () => api.get('/admin/activity/live'),
  getCustomerActivity: (userId, limit = 100) => api.get(`/admin/activity/customer/${userId}?limit=${limit}`),
};

// Preview Links (Admin)
export const previewLinks = {
  create: (note) => api.post('/admin/preview-links', { note }),
  getAll: () => api.get('/admin/preview-links'),
  revoke: (token) => api.delete(`/admin/preview-links/${token}`),
};

// Preview (Public - no auth needed)
export const preview = {
  claim: (token, deviceId) => axios.post(`${API_URL}/api/preview/${token}/claim`, { device_id: deviceId }),
  validate: (token, deviceId) => axios.get(`${API_URL}/api/preview/${token}/validate`, { params: { device_id: deviceId } }),
  getProducts: (token, deviceId, params = {}) => axios.get(`${API_URL}/api/preview/${token}/products`, { params: { device_id: deviceId, ...params } }),
  getCategories: (token, deviceId) => axios.get(`${API_URL}/api/preview/${token}/categories`, { params: { device_id: deviceId } }),
};

// Seed
export const seed = () => api.post('/seed');

export default api;
